import { AfterViewChecked, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Error } from 'src/sb-tools/typescript/enums/error.enum';
import { CommonUserService } from 'src/sb-tools/typescript/services/common-user.service';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { CategoryService } from 'src/sb-tools/typescript/services/category.service';
import { CustomerConfigurationService } from './core/services/customer-configuration.service';
import { forkJoin, Subject } from 'rxjs';
import { LocalizationLanguage } from 'src/sb-tools/typescript/enums/localization-language.enum';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { TopMenuComponent } from './top-menu/top-menu.component';
import { TagService } from '@sb-tools/typescript/services/tag.service';
import { SignalRService } from './core/services/signalR.service';
import { RightPanelService } from './core/services/right-panel.service';
import { WindowRef } from 'src/sb-tools/typescript/services/window-ref.service';
import { ComponentMessengerService } from './core/services/component-messenger.service';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { BrowserNotificationsService } from './core/services/bowser-notification.service';
import { TicketNotificationsService } from './core/services/ticket-notification.service';
import { RightPanelType } from './core/enums/right-panel-type.enum';
import { TicketRightPanelService } from './core/services/ticket-right-panel.service';
import { UserDataService } from 'src/sb-tools/typescript/services/user-data.service';
import { UserRole } from '@sb-tools/typescript/enums/user-role.enum';
import Color from 'color';
import { ReplyTemplateService } from 'src/sb-modules/Frontend-Reply-Templates-Module/core/reply-template.service';
import { UserAvatarService } from 'src/sb-modules/Frontend-User-Avatar-Module/user-avatar.service';
import { UpToDateBuildService } from '../build-details/up-to-date.service';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { environment } from '@environment';
import { EncodeHtmlSymbolsPipe } from '@sb-tools/typescript/pipes/encode-html-symbols.pipe';
import { QuillHelperService } from '@sb-tools/typescript/services/quill/quill-helper.service';
import { finalize, take } from 'rxjs/operators';
import { AppInsightsService } from '@sb-tools/typescript/services/app-insights.service';
import { AppsService } from './core/services/apps.service';
import { HelperService } from '@sb-tools/typescript/services/helper.service';
import { TicketDetailsPanelService } from './core/services/ticket-details-panel.service';
import { ReplyTemplateDataService } from '../sb-modules/Frontend-Reply-Templates-Module/core/reply-template-data.service';
import { ThemeService } from '@sb-tools/typescript/services/theme.service';
import { resolveIsOnlyMyTicketsFilterDefault } from './core/utils/resolve-default-ticket-filter.util';
import { AllReplyTemplatesModel } from '@sb-tools/typescript/models/all-reply-templates-model.model';
import { TicketAssignedEventModel } from './core/models/dto/signalR/ticket-assigned-event-model.model';
import { TicketAddedEventModel } from './core/models/dto/signalR/ticket-added-event-model.model';
import { NotifyAboutNewReplyEventModel } from './core/models/dto/signalR/notify-about-new-reply-event-model.model';
import { NotifyAboutNewSurveyResponseAddedEventModel } from './core/models/dto/signalR/notify-about-new-survey-response-added-event-model.model';
import { AppDTO } from '@sb-tools/typescript/models/dto/app.dto';
import { NgClass } from '@angular/common';
import { AccessDeniedPageComponent } from '@sb-tools/typescript/components/error-pages/access-denied-page/access-denied-page.component';
import { SimpleNotificationsModule } from 'angular2-notifications';
import { OnboardingLandingModalComponent } from './shared/modal-windows/onboarding-landing-modal/onboarding-landing-modal.component';

const BLACK_COLOR = '#000000';
const WHITE_COLOR = '#ffffff';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        NgClass,
        RouterOutlet,
        SimpleNotificationsModule,
        OnboardingLandingModalComponent,
        TopMenuComponent,
        AccessDeniedPageComponent,
    ],
})
export class AppComponent implements OnInit, AfterViewChecked, OnDestroy {
    error = Error.sessionExpired;
    isAppInitialized = false;
    isOneTime: boolean;
    isRefOnboarding: boolean;
    isSystemAdmin = false;

    private isWindowFocused = false;
    private unsubscriber = new Subject<void>();

    constructor(
        private commonUserService: CommonUserService,
        private commonDataService: CommonDataService,
        private categoryService: CategoryService,
        private customerConfigurationService: CustomerConfigurationService,
        private activatedRoute: ActivatedRoute,
        public ticketDetailsPanelService: TicketDetailsPanelService,
        private userDataService: UserDataService,
        private tagService: TagService,
        private signalRService: SignalRService,
        public rightPanelService: RightPanelService,
        public ticketRightPanelService: TicketRightPanelService,
        private windowRef: WindowRef,
        private componentMessengerService: ComponentMessengerService,
        private browserNotificationsService: BrowserNotificationsService,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef,
        private ticketNotificationsService: TicketNotificationsService,
        private userAvatarService: UserAvatarService,
        private replyTemplateService: ReplyTemplateService,
        private replyTemplateDataService: ReplyTemplateDataService,
        private appInsightsService: AppInsightsService,
        private upToDateService: UpToDateBuildService,
        private bsLocaleService: BsLocaleService,
        private encodeHtmlSymbolsPipe: EncodeHtmlSymbolsPipe,
        private quillHelperService: QuillHelperService,
        private applicationsService: AppsService,
        private themeService: ThemeService,
    ) {
        this.upToDateService.buildIsUpToDate.pipe(takeUntil(this.unsubscriber), take(1)).subscribe();
    }

    ngOnInit(): void {
        if (HelperService.checkIsInIframe()) {
            window.addEventListener('message', this.receiveMessage.bind(this), false);
        }

        if (window.location.pathname === '/404') {
            this.isAppInitialized = true;
            return;
        }
        this.browserNotificationsService.requestPermission().pipe(takeUntil(this.unsubscriber), take(1)).subscribe();

        // this language will be used as a fallback when a translation isn't found in the current language
        this.translateService.addLangs(['nb']);

        this.isOneTime = this.commonDataService.isOneTime;

        const customerConfiguration = this.customerConfigurationService.get();

        this.appInsightsService.init();

        forkJoin([this.commonUserService.getFullInfo(), customerConfiguration])
            .pipe(takeUntil(this.unsubscriber), take(1))
            .subscribe({
                next: (results) => {
                    this.commonDataService.user = results[0];
                    this.commonDataService.customerSettings = results[1];
                    environment.signalrServerUrl = results[1].heartbeatUrl;

                    this.themeService.setTheme(this.commonDataService.user.theme);
                    this.setUserInfoInHiddenHtmlBlockForExtensions();

                    if (this.commonDataService.user.role === UserRole.manager) {
                        this.commonDataService.user.isAdmin = true;
                    }

                    if (this.commonDataService.user.role === UserRole.systemAdmin) {
                        this.isSystemAdmin = true;
                    }

                    if (this.commonDataService.user.role === UserRole.readOnlyUser) {
                        this.commonDataService.user.isReadOnly = true;

                        // todo remove when user constraints with access is ready
                        this.commonDataService.user.hasAccessToAgentData =
                            this.commonDataService.customerSettings.readOnlyUsersAccessSettings.agentData;
                        this.commonDataService.user.hasAccessToAttachments =
                            this.commonDataService.customerSettings.readOnlyUsersAccessSettings.attachments;
                        this.commonDataService.user.hasAccessToInlineImages =
                            this.commonDataService.customerSettings.readOnlyUsersAccessSettings.inlineImages;
                        this.commonDataService.user.hasAccessToNotes =
                            this.commonDataService.customerSettings.readOnlyUsersAccessSettings.notes;
                        this.commonDataService.user.hasAccessToTicketLog =
                            this.commonDataService.customerSettings.readOnlyUsersAccessSettings.ticketLog;
                    } else {
                        this.commonDataService.user.hasAccessToAgentData = true;
                        this.commonDataService.user.hasAccessToAttachments = true;
                        this.commonDataService.user.hasAccessToInlineImages = true;
                        this.commonDataService.user.hasAccessToNotes = true;
                        this.commonDataService.user.hasAccessToTicketLog = true;
                    }

                    if (this.commonDataService.user.localizationLanguage) {
                        this.setupLanguage(this.commonDataService.user.localizationLanguage);
                    } else {
                        this.setupLanguage(this.commonDataService.customerSettings.language);
                    }

                    this.isAppInitialized = true;

                    this.signalRService.reconnect();

                    this.categoryService
                        .get(true)
                        .pipe(takeUntil(this.unsubscriber), take(1))
                        .subscribe((categories) => {
                            this.commonDataService.categories.all = categories;
                            this.commonDataService.categories.folder = [...categories].sort((a, b) => a.id - b.id);

                            if (this.commonDataService.user.isAdmin) {
                                this.commonDataService.categories.private = this.commonDataService.categories.all;
                            }
                        });

                    if (!this.commonDataService.user.isAdmin) {
                        this.categoryService
                            .get(false)
                            .pipe(takeUntil(this.unsubscriber), take(1))
                            .subscribe((categories) => {
                                this.commonDataService.categories.private = categories;
                            });
                    }

                    this.commonDataService.isOnlyMyTicketsFilterDefault = resolveIsOnlyMyTicketsFilterDefault(
                        this.commonDataService.user,
                        this.commonDataService.customerSettings as any,
                    );

                    this.userAvatarService.labelBgColor = this.commonDataService.customerSettings.brandColor;
                    this.userAvatarService.labelTextColor = Color(this.userAvatarService.labelBgColor).isLight()
                        ? BLACK_COLOR
                        : WHITE_COLOR;

                    if (this.commonDataService.user.role === UserRole.vendor) {
                        this.commonDataService.customerSettings.isTicketAssigningObligatory = false;
                    }
                },
                error: (error) => {
                    if (this.isOneTime && error.status === 403 && [Error.linkIsExpired].includes(error.error.errorId)) {
                        this.isAppInitialized = true;
                    }
                },
            });

        this.userDataService
            .getSignatures()
            .pipe(takeUntil(this.unsubscriber), take(1))
            .subscribe((signatures) => {
                if (signatures.socialBoards.text) {
                    signatures.socialBoards.text = this.quillHelperService.convertImageSizeFromStylesToAttributes(
                        signatures.socialBoards.text,
                    );
                }
                this.commonDataService.signatures = signatures;
            });

        if (!this.commonDataService.isOneTime) {
            forkJoin([this.tagService.getAll(), this.tagService.getAll(false)])
                .pipe(takeUntil(this.unsubscriber), take(1))
                .subscribe((results) => {
                    this.tagService.globalTags = [...results[0]].sort((a, b) =>
                        a.text.toLowerCase().localeCompare(b.text.toLowerCase()),
                    );

                    this.tagService.notGlobalTags = [...results[1]].sort((a, b) =>
                        a.text.toLowerCase().localeCompare(b.text.toLowerCase()),
                    );

                    this.tagService.allTags = [...this.tagService.globalTags, ...this.tagService.notGlobalTags].sort(
                        (a, b) => a.text.toLowerCase().localeCompare(b.text.toLowerCase()),
                    );
                });

            this.applicationsService
                .getApps()
                .pipe(takeUntil(this.unsubscriber), take(1))
                .subscribe((apps: AppDTO[]) => {
                    this.commonDataService.apps = apps;
                });

            this.customerConfigurationService
                .getOutboundEmailAddresses()
                .pipe(takeUntil(this.unsubscriber), take(1))
                .subscribe((emails) => {
                    this.commonDataService.outboundEmailAddresses = emails;
                });

            this.loadReplyTemplates();
        }

        this.windowRef.nativeWindow.onfocus = () => {
            this.signalRService.windowFocusStateChanged(true).pipe(take(1)).subscribe();
            this.isWindowFocused = true;
            this.componentMessengerService.needToReload();
        };

        this.windowRef.nativeWindow.onblur = (e) => {
            if (e.target === window) {
                this.isWindowFocused = false;
                this.signalRService.windowFocusStateChanged(false).pipe(take(1)).subscribe();
            }
        };

        this.signalRService
            .onTicketAdded()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: TicketAddedEventModel) => {
                this.ticketNotificationsService.showNewTicketNotification(data);
            });

        this.signalRService
            .onNotifyAboutNewReply()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: NotifyAboutNewReplyEventModel) => {
                this.ticketNotificationsService.showNewMessageNotification(data, this.isWindowFocused);
            });

        this.signalRService
            .onTicketAssigned()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: TicketAssignedEventModel) => {
                this.ticketNotificationsService.showAssignNotification(data);
            });

        this.signalRService
            .onNotifyAboutSurveyResponseAdded()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((notification: NotifyAboutNewSurveyResponseAddedEventModel) => {
                this.ticketNotificationsService.showSurveyNotification(notification.notification);
            });

        // todo: rename userId attribute
        document.addEventListener('click', (event: Event): void => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            const element = target.closest('.user-info-link') || target.closest('.mention-group');
            if (!element) {
                return;
            }
            event.stopPropagation();
            event.preventDefault();

            if (element.classList.contains('user-info-link')) {
                const userId = element.getAttribute('userId') ?? element.getAttribute('userid');
                if (userId) {
                    this.rightPanelService.open(RightPanelType.user, { userId });
                }
                return;
            }
            if (element.classList.contains('mention-group')) {
                const groupId = element.getAttribute('data-mention-id');
                if (groupId) {
                    this.rightPanelService.open(RightPanelType.groupInfo, { groupId });
                }
            }
        });

        // todo: it should be unsubscribed when modal is shown
        this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscriber)).subscribe((params) => {
            if (params['ref'] === 'onboarding') {
                this.isRefOnboarding = true;
            }
        });
    }

    setupLanguage(language: LocalizationLanguage): void {
        let locale: string;

        switch (language) {
            case LocalizationLanguage.norwegian:
                this.commonDataService.language = 'nb';
                locale = 'nb';
                this.translateService.use('nb');
                break;
            case LocalizationLanguage.swedish:
                this.commonDataService.language = 'sv_SE';
                locale = 'sv';
                this.translateService.use('sv_SE');
                break;
            case LocalizationLanguage.danish:
                this.commonDataService.language = 'da_DK';
                locale = 'da';
                this.translateService.use('da_DK');
                break;
            case LocalizationLanguage.finnish:
                this.commonDataService.language = 'fi';
                locale = 'fi';
                this.translateService.use('fi');
                break;
            default:
                this.commonDataService.language = 'en';
                locale = 'en';
                this.translateService.use(undefined);
        }

        this.bsLocaleService.use(locale);
    }

    ngAfterViewChecked(): void {
        this.cdr.detectChanges();
    }

    private loadReplyTemplates(): void {
        this.replyTemplateDataService
            .getAllReplyTemplates()
            .pipe(
                finalize(() => {
                    this.replyTemplateService.isReplyTemplatesLoaded = true;
                }),
                takeUntil(this.unsubscriber),
                take(1),
            )
            .subscribe(({ community, personal }: AllReplyTemplatesModel) => {
                this.replyTemplateService.replyTemplatesTree = community;
                this.replyTemplateService.personalReplyTemplatesTree = personal;
                this.replyTemplateService.personalReplyTemplates = [...personal.byCategories, ...personal.global];
                this.replyTemplateService.replyTemplates = [...community.byCategories, ...community.global];
                this.replyTemplateService.allReplyTemplates = this.replyTemplateService.personalReplyTemplates.concat(
                    this.replyTemplateService.replyTemplates,
                );
            });
    }

    private receiveMessage(event): void {
        if (event.data && event.data.eventName === 'openSingleTicket') {
            this.ticketDetailsPanelService.open(event.data.ticketId, false, false, true);
        }
    }

    private setUserInfoInHiddenHtmlBlockForExtensions(): void {
        [
            ['current-user-info-id', this.commonDataService.user.id],
            ['current-user-info-email', this.commonDataService.user.email],
            ['current-user-info-name', this.commonDataService.user.name],
        ].forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            document.body.appendChild(input);
        });
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
